import React, { useState, useEffect } from 'react';
import { firestore } from '../config/firebaseConfig';
import { collection, getDocs, query, where, orderBy, addDoc, doc, updateDoc } from 'firebase/firestore';
import axios from 'axios';
import './Simulador.css';

const apiUrl = process.env.REACT_APP_API_URL;
const tenantId = localStorage.getItem('tenantId') || 'unknownTenant';
let sanitizedClientId = '';
if (tenantId) {
    sanitizedClientId = tenantId;
}
const SIMULADOR_API_URL = `${apiUrl}/send-message?sanitizedClientId=${sanitizedClientId}`;

const Simulador = ({ specificQuestionId = null, onCancel }) => {
    const [questions, setQuestions] = useState([]);
    const [students, setStudents] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [sendingDetails, setSendingDetails] = useState({ students: [] });
    const [isSending, setIsSending] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasSentSuccessfully, setHasSentSuccessfully] = useState(false);

    const markQuestionAsSent = async (questionId, currentPriority) => {
        try {
            const questionRef = doc(firestore, 'questoes', questionId);
            const updates = { enviado: true };
            if (currentPriority > 0) {
                updates.prioridade = 0;
            }
            await updateDoc(questionRef, updates);
        } catch (error) {
            console.error('Erro ao atualizar questão:', error);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                setHasSentSuccessfully(false);
                
                const questoesRef = collection(firestore, 'questoes');
                const questoesQuery = query(
                    questoesRef,
                    where('bloqueado', '==', false),
                    where('tenantId', '==', tenantId),
                    orderBy('createdAt', 'asc')
                );
                const questoesSnapshot = await getDocs(questoesQuery);
                const questoesData = questoesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setQuestions(questoesData);

                const contatosRef = collection(firestore, 'contatos');
                const contatosQuery = query(
                    contatosRef,
                    where('status', '==', 'Ativo'),
                    where('tenantId', '==', tenantId)
                );
                const contatosSnapshot = await getDocs(contatosQuery);
                const contatosData = contatosSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setStudents(contatosData);

                if (specificQuestionId) {
                    const foundQuestion = questoesData.find(q => q.id === specificQuestionId);
                    if (foundQuestion) {
                        setSelectedQuestion(foundQuestion);
                    } else {
                        setError('Questão não encontrada ou está bloqueada');
                    }
                }
                setError('');
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
                setError('Houve um problema ao buscar dados.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [tenantId, specificQuestionId]);

    const formatQuestionMessage = (question) => {
        if (!question) return '';
        let message = `*${question.enunciado.trim()}*`;

        if (question.type === 'quiz' || question.type === 'pesquisa_multipla_escolha') {
            if (question.options && Array.isArray(question.options) && question.options.length > 0) {
                const optionsText = question.options
                    .map((option, index) => {
                        if (option.text && option.text.trim() !== '') {
                            return `${String.fromCharCode(65 + index)}) ${option.text}`;
                        }
                        return null;
                    })
                    .filter(Boolean) // Remove opções nulas ou vazias
                    .join('\n\n');

                if (optionsText) {
                    message += `\n\n_Alternativas:_\n\n${optionsText}`;
                    message += `\n\n_Responda com a letra da alternativa._`;
                } else {
                    message += `\n\n_Por favor, digite sua resposta._`;
                }
            } else {
                message += `\n\n_Por favor, digite sua resposta._`;
            }
        } else if (question.type === 'pesquisa_dissertativa') {
            message += `\n\n_Por favor, digite sua resposta._`;
        } else {
            // Fallback para tipos desconhecidos
            message += `\n\n_Por favor, digite sua resposta._`;
        }
        return message;
    };

    const findCorrectAnswer = (options) => {
        if (!options || !Array.isArray(options)) {
            return { answer: 'Não disponível', justification: '' };
        }
        const correctOption = options.find((option) => option.isCorrect);
        return correctOption
            ? {
                answer: String.fromCharCode(65 + options.indexOf(correctOption)),
                justification: correctOption.justificativa || '',
            }
            : {
                answer: 'Não disponível',
                justification: '',
            };
    };

    // =================================================================
    //          *** FUNÇÃO CORRIGIDA PARA INCLUIR O TIPO ***
    // =================================================================
    const logSendDetails = async (student, question, correctAnswer, justification) => {
        try {
            await addDoc(collection(firestore, 'logEnvioQuestao'), {
                idcontato: student.id,
                telefone: String(student.telefone),
                idquestao: question.id,
                enunciado: question.enunciado,
                type: question.type, 
                respostacorreta: correctAnswer,
                justificativa: justification || '',
                status: 'P',
                timestamp: new Date(),
                tenantId: tenantId
            });
        } catch (error) {
            console.error('Erro ao registrar log de envio:', error);
        }
    };

    const getQuestoesNaoEnviadasParaContato = async (student) => {
        try {
            const logEnvioQuestaoRef = collection(firestore, 'logEnvioQuestao');
            const envioQuery = query(
                logEnvioQuestaoRef,
                where('idcontato', '==', student.id),
                where('tenantId', '==', tenantId)
            );
            const envioSnapshot = await getDocs(envioQuery);
            const questoesEnviadasIds = new Set(
                envioSnapshot.docs.map((doc) => doc.data().idquestao)
            );

            let questoesNaoEnviadas = questions.filter(
                (questao) => !questoesEnviadasIds.has(questao.id)
            );

            questoesNaoEnviadas = questoesNaoEnviadas.sort((a, b) => {
                const dateA = a.createdAt?.seconds || 0;
                const dateB = b.createdAt?.seconds || 0;
                return dateA - dateB;
            });

            return questoesNaoEnviadas;
        } catch (error) {
            console.error('Erro ao obter questões não enviadas para o contato:', error);
            return [];
        }
    };

    const sendMessagesToStudents = async () => {
        if (isSending || hasSentSuccessfully) return;
        setIsSending(true);
        setError('');
        setSuccess('');
        setHasSentSuccessfully(false);

        let foundQuestionToSend = false;
        const updatedSendingDetails = [];

        if (specificQuestionId) {
            const questionExists = questions.some(q =>
                q.id === specificQuestionId && !q.bloqueado
            );

            if (!questionExists) {
                setError('Questão não encontrada ou está bloqueada');
                setIsSending(false);
                return;
            }
        }

        for (let student of students) {
            let questionToSend = null;

            if (specificQuestionId) {
                questionToSend = questions.find(q =>
                    q.id === specificQuestionId && !q.bloqueado
                );
            } else {
                const questoesNaoEnviadas = await getQuestoesNaoEnviadasParaContato(student);
                questionToSend = questoesNaoEnviadas.find(q => q.prioridade > 0) || questoesNaoEnviadas[0];
            }

            if (questionToSend) {
                foundQuestionToSend = true;
                setSelectedQuestion(questionToSend);

                updatedSendingDetails.push({
                    telefone: student.telefone,
                    idQuestao: questionToSend.id,
                    data: new Date().toLocaleDateString(),
                });

                try {
                    await axios.post(SIMULADOR_API_URL, {
                        sanitizedClientId,
                        phoneNumber: student.telefone,
                        message: formatQuestionMessage(questionToSend),
                    });

                    const { answer: correctAnswer, justification } = findCorrectAnswer(
                        questionToSend.options
                    );

                    // Chamada da função atualizada para passar o objeto da questão
                    await logSendDetails(
                        student,
                        questionToSend, // Passando o objeto inteiro
                        correctAnswer,
                        justification
                    );

                    await markQuestionAsSent(questionToSend.id, questionToSend.prioridade);
                } catch (error) {
                    console.error('Erro ao enviar mensagem:', error);
                    // o backend explica o motivo (ex.: contato mandou SAIR, bot desligado)
                    setError(error.response?.data?.error || 'Houve um problema ao enviar a mensagem.');
                    break;
                }
            }
        }

        if (!foundQuestionToSend && !error) {
            setError(specificQuestionId
                ? 'Questão não encontrada ou já enviada para todos os contatos'
                : 'Sem questões disponíveis no momento');
        } else if (foundQuestionToSend && !error) {
            setSuccess('Mensagens enviadas com sucesso!');
            setHasSentSuccessfully(true);
        }

        setSendingDetails({ students: updatedSendingDetails });
        setIsSending(false);
    };

    const getConsolidatedDetails = () => {
        const consolidated = {};
        sendingDetails.students.forEach((student) => {
            const { idQuestao } = student;
            if (consolidated[idQuestao]) {
                consolidated[idQuestao].count += 1;
            } else {
                consolidated[idQuestao] = {
                    count: 1,
                    question: questions.find(q => q.id === idQuestao)
                };
            }
        });
        return consolidated;
    };

    const consolidatedDetails = getConsolidatedDetails();

    if (isLoading) {
        return (
            <div className="modal-content">
                <div className="loading-indicator">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="modal-content">
            <h3>{specificQuestionId ? 'Envio Expresso' : 'Fila de Questões'}</h3>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            {selectedQuestion && (
                <div className="question-preview">
                    <h4>Questão Selecionada:</h4>
                    <p><strong>ID:</strong> {selectedQuestion.id}</p>
                    <p><strong>Enunciado:</strong> {selectedQuestion.enunciado}</p>
                    {selectedQuestion.prioridade > 0 && (
                        <p><strong>Prioridade:</strong> {selectedQuestion.prioridade}</p>
                    )}
                </div>
            )}

            <div className="action-buttons">
                <button
                    onClick={sendMessagesToStudents}
                    disabled={isSending || error || hasSentSuccessfully}
                    className="confirm-button"
                >
                    {isSending ? 'Enviando...' : 'Confirmar Envio'}
                </button>
                <button
                    onClick={onCancel}
                    className="cancel-button"
                    disabled={isSending}
                >
                    Fechar
                </button>
            </div>

            {isSending && (
                <div className="sending-progress">
                    <p>Enviando para {students.length} contatos...</p>
                </div>
            )}

            {Object.keys(consolidatedDetails).length > 0 && (
                <div className="sending-details">
                    <h4>Resumo do Envio:</h4>
                    <ul>
                        {Object.entries(consolidatedDetails).map(([idQuestao, detail]) => (
                            <li key={idQuestao}>
                                <strong>Questão ID: {idQuestao}</strong> -
                                Enviada para {detail.count} {detail.count > 1 ? 'contatos' : 'contato'}
                                {detail.question && (
                                    <div className="question-summary">
                                        <p>{detail.question.enunciado}</p>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default Simulador;